import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, InputNumber, Switch, Tag, Space, message, Popconfirm,
  Divider, Alert, Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  fetchPolicies, createPolicy, updatePolicy, disablePolicy, fetchSecurityMeta, assignPolicy,
} from '../../api';
import { fetchUsers } from '../../../permissionSet/api';

const { Text } = Typography;

// The policy types that MUST carry a rotation interval. Hardcoded rather than
// read from /security/meta so the form still behaves correctly if that call is
// slow or the user lacks permission for it — meta only supplies nicer labels.
const PASSWORD_POLICY_TYPES = ['PASSWORD_EXPIRY'];

// Interval units. Everything is stored as a plain day count on the policy; the
// unit exists so an admin can say "2 months" instead of doing the arithmetic.
// `max` keeps the entered value inside the backend's 1..3650 day ceiling
// whichever unit is picked.
const UNITS = [
  { label: 'Days', value: 'DAYS', days: 1, max: 3650 },
  { label: 'Months', value: 'MONTHS', days: 30, max: 120 },
  { label: 'Years', value: 'YEARS', days: 365, max: 10 },
];
const unitDays = (u) => UNITS.find((x) => x.value === u)?.days || 1;
const unitMax = (u) => UNITS.find((x) => x.value === u)?.max || 3650;

// Quick-picks — they just fill the value/unit pair below.
const EXPIRY_PRESETS = [
  { label: '30 days', value: 30, unit: 'DAYS' },
  { label: '45 days', value: 45, unit: 'DAYS' },
  { label: '2 months', value: 2, unit: 'MONTHS' },
  { label: '3 months', value: 3, unit: 'MONTHS' },
  { label: '6 months', value: 6, unit: 'MONTHS' },
  { label: '1 year', value: 1, unit: 'YEARS' },
];

// Split a stored day count back into the friendliest value/unit pair for editing.
const daysToParts = (days) => {
  const d = Number(days);
  if (!d || d < 1) return { value: null, unit: 'DAYS' };
  if (d % 365 === 0) return { value: d / 365, unit: 'YEARS' };
  if (d % 30 === 0) return { value: d / 30, unit: 'MONTHS' };
  return { value: d, unit: 'DAYS' };
};

// Surfaces the gateway's { pendingApproval } vs { applied } result uniformly.
const reportResult = (res, okMsg) => {
  if (res?.pendingApproval) message.info('Submitted for approval — the change will take effect once approved.');
  else message.success(okMsg);
};

const LoginPoliciesTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [policyTypes, setPolicyTypes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();

  // Read straight off the form rather than mirroring it in local state, so the
  // rotation fields react to every way the values can change (select, preset
  // buttons, setFieldsValue when editing).
  const policyType = Form.useWatch('policyType', form);
  const expiryValue = Form.useWatch('expiryValue', form);
  const expiryUnit = Form.useWatch('expiryUnit', form);

  const isPasswordType = PASSWORD_POLICY_TYPES.includes(policyType);
  const expiryDays = expiryValue > 0 ? expiryValue * unitDays(expiryUnit) : null;

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchPolicies();
      setData(res.data || []);
    } catch (e) { message.error('Failed to load policies'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    fetchSecurityMeta()
      .then((m) => {
        // Prefer the labelled options; fall back to the raw enum list for an
        // older backend that only returns policyTypes.
        setPolicyTypes(m.policyTypeOptions || (m.policyTypes || []).map((t) => ({ value: t, label: t })));
      })
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null); form.resetFields();
    form.setFieldsValue({
      enabled: true, priority: 0,
      expiryValue: null, expiryUnit: 'DAYS',
      passwordExpiryWarnDays: 7, passwordHistoryCount: 3, passwordMinLength: 8,
    });
    setModalOpen(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    const { value, unit } = daysToParts(rec.passwordExpiryDays);
    form.setFieldsValue({
      ...rec,
      expiryValue: value,
      expiryUnit: unit,
      passwordExpiryWarnDays: rec.passwordExpiryWarnDays ?? 7,
      passwordHistoryCount: rec.passwordHistoryCount ?? 3,
      passwordMinLength: rec.passwordMinLength ?? 8,
    });
    setModalOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    // expiryValue/expiryUnit are UI-only — the policy stores a day count.
    const { expiryValue: v, expiryUnit: u, ...rest } = values;
    // Only the password-expiry type carries a rotation schedule. Sending an
    // explicit null for every other type clears any interval left behind by a
    // policy that used to be one — the backend would otherwise keep applying
    // it invisibly, since it treats rotation as orthogonal to the device rule.
    const forPasswordType = PASSWORD_POLICY_TYPES.includes(values.policyType);
    const payload = {
      ...rest,
      passwordExpiryDays: forPasswordType && v > 0 ? v * unitDays(u) : null,
    };
    try {
      const res = editing ? await updatePolicy(editing._id, payload) : await createPolicy(payload);
      reportResult(res, editing ? 'Policy updated' : 'Policy created');
      setModalOpen(false); load();
    } catch (e) { message.error(e?.response?.data?.message || 'Save failed'); }
  };

  const onDisable = async (rec) => {
    try { reportResult(await disablePolicy(rec._id), 'Policy disabled'); load(); }
    catch (e) { message.error(e?.response?.data?.message || 'Disable failed'); }
  };

  const openAssign = async () => {
    assignForm.resetFields();
    setAssignOpen(true);
    try { const res = await fetchUsers({ limit: 200 }); setUsers(res.data || []); } catch { /* ignore */ }
  };
  const submitAssign = async () => {
    const values = await assignForm.validateFields();
    try {
      reportResult(await assignPolicy(values), 'Policy assigned to employee');
      setAssignOpen(false);
    } catch (e) { message.error(e?.response?.data?.message || 'Assignment failed'); }
  };

  const columns = [
    { title: 'Code', dataIndex: 'code' },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Type', dataIndex: 'policyType', render: (t) => <Tag color="blue">{t}</Tag> },
    {
      title: 'Password Rotation',
      dataIndex: 'passwordExpiryDays',
      render: (d, rec) => {
        if (!(d > 0)) return <Text type="secondary">—</Text>;
        const { value, unit } = daysToParts(d);
        const label = `${value} ${UNITS.find((u) => u.value === unit).label.toLowerCase()}`;
        return (
          <Tag color="gold">
            Every {label}
            {rec.passwordHistoryCount > 0 ? ` · no reuse of last ${rec.passwordHistoryCount}` : ''}
          </Tag>
        );
      },
    },
    { title: 'Priority', dataIndex: 'priority' },
    { title: 'Enabled', dataIndex: 'enabled', render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>) },
    {
      title: 'Actions', render: (_, rec) => (
        <Space>
          <a onClick={() => openEdit(rec)}>Edit</a>
          {rec.enabled && (
            <Popconfirm title="Disable this policy?" onConfirm={() => onDisable(rec)}>
              <a>Disable</a>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Policy</Button>
        <Button onClick={openAssign}>Assign Policy to Employee</Button>
      </Space>
      <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" />

      <Modal
        title={editing ? 'Edit Policy' : 'New Policy'}
        open={modalOpen}
        onOk={submit}
        onCancel={() => setModalOpen(false)}
        width={620}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {!editing && (
            <Form.Item name="code" label="Code (optional)"><Input placeholder="Auto from name if blank" /></Form.Item>
          )}
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="policyType" label="Policy Type" rules={[{ required: true }]}>
            <Select
              placeholder="Choose what this policy enforces"
              options={policyTypes.map((t) => ({
                label: t.label || t.value,
                value: t.value,
                title: t.description,
              }))}
              optionRender={(opt) => (
                <div>
                  <div>{opt.label}</div>
                  {opt.data.title && <Text type="secondary" style={{ fontSize: 12 }}>{opt.data.title}</Text>}
                </div>
              )}
            />
          </Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>

          {/* Rotation settings belong to the Force Periodic Password Change
              type only — every other type is a device/network rule and has no
              password schedule to configure. */}
          {isPasswordType && (
            <>
              <Divider orientation="left" plain>Password Rotation</Divider>

              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Users on this policy can still sign in after their password expires, but land straight on the change-password screen and are blocked from everything else until they set a new one."
              />

              <Form.Item label="Force a password change every" required style={{ marginBottom: 8 }}>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item
                    name="expiryValue"
                    noStyle
                    rules={[{ required: true, message: 'Enter how often the password must change' }]}
                  >
                    <InputNumber min={1} max={unitMax(expiryUnit)} style={{ width: '60%' }} placeholder="e.g. 30" />
                  </Form.Item>
                  <Form.Item name="expiryUnit" noStyle initialValue="DAYS">
                    <Select options={UNITS.map(({ label, value }) => ({ label, value }))} style={{ width: '40%' }} />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>

              <Space wrap style={{ marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Quick pick:</Text>
                {EXPIRY_PRESETS.map((p) => (
                  <Button
                    key={p.label}
                    size="small"
                    type={expiryValue === p.value && expiryUnit === p.unit ? 'primary' : 'default'}
                    onClick={() => form.setFieldsValue({ expiryValue: p.value, expiryUnit: p.unit })}
                  >
                    {p.label}
                  </Button>
                ))}
              </Space>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {expiryDays
                    ? `Counted from the user's last password change — ${expiryDays} day${expiryDays === 1 ? '' : 's'}. Users who have never changed their password are measured from their account creation date.`
                    : 'Choose how often users on this policy must set a new password.'}
                </Text>
              </div>

              <Form.Item
                name="passwordExpiryWarnDays"
                label="Warn the user this many days before expiry"
                rules={[{
                  validator: (_, v) => (v == null || !expiryDays || v < expiryDays
                    ? Promise.resolve()
                    : Promise.reject(new Error('Must be smaller than the rotation interval'))),
                }]}
              >
                <InputNumber min={0} max={365} style={{ width: '100%' }} addonAfter="days" />
              </Form.Item>
              <Form.Item
                name="passwordHistoryCount"
                label="Block reuse of the last N passwords"
                extra="0 still blocks reusing the current password."
              >
                <InputNumber min={0} max={24} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="passwordMinLength" label="Minimum password length">
                <InputNumber min={6} max={64} style={{ width: '100%' }} addonAfter="characters" />
              </Form.Item>
              <Divider plain />
            </>
          )}

          <Form.Item name="priority" label="Priority"><InputNumber min={0} /></Form.Item>
          <Form.Item name="approvalRequired" label="Approval Required (informational)" valuePropName="checked"><Switch /></Form.Item>
          {editing && <Form.Item name="enabled" label="Enabled" valuePropName="checked"><Switch /></Form.Item>}
        </Form>
      </Modal>

      <Modal title="Assign Login Policy to Employee" open={assignOpen} onOk={submitAssign} onCancel={() => setAssignOpen(false)} destroyOnClose>
        <Form form={assignForm} layout="vertical">
          <Form.Item name="employeeId" label="Employee" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label"
              options={users.map((u) => ({ label: `${u.name} (${u.employeeCode || u.email})`, value: u._id }))} />
          </Form.Item>
          <Form.Item name="loginPolicyId" label="Login Policy (leave blank to clear)">
            <Select allowClear options={data.map((p) => ({ label: `${p.name} (${p.policyType})`, value: p._id }))} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default LoginPoliciesTab;
